<template>
  <div class="dynamic-form">
    <div
      v-for="(section, sectionIndex) in formConfig"
      :key="sectionIndex"
      class="form-section"
    >
      <div class="section-header" @click="toggleOptional(sectionIndex)">
        <span class="section-title">{{ section.tit }}</span>
        <van-icon
          v-if="hasOptionalFields(section)"
          :name="sectionExpanded[sectionIndex] ? 'arrow-up' : 'arrow-down'"
          class="toggle-icon"
        />
      </div>

      <van-cell-group inset v-if="hasRequiredFields(section)">
        <template v-for="(item, itemIndex) in getRequiredFields(section)">
          <slot
            v-if="$slots[item.name] || item.type === 'slot'"
            :name="item.name"
          >
          </slot>
          <template v-else-if="item.type === 'switch'">
            <van-field
              :key="'required-' + sectionIndex + '-' + itemIndex"
              :name="item.name"
              :label="item.label"
              :required="!!item.required"
              :disabled="item.disabled"
            >
              <template #input>
                <van-switch
                  v-model="formData[item.name]"
                  :disabled="item.disabled"
                  size="24px"
                />
              </template>
            </van-field>
          </template>
          <van-field
            v-else
            :key="'required-' + sectionIndex + '-' + itemIndex"
            :name="item.name"
            :label="item.label"
            :placeholder="getPlaceholder(item)"
            :required="!!item.required"
            :disabled="item.disabled"
            :readonly="item.type === 'select' || item.readonly"
            :rules="item.rules"
            :value="
              item.type === 'select'
                ? item.options.find((opt) => opt.value === formData[item.name])
                    ?.text || ''
                : formData[item.name]
            "
            :type="item.type === 'select' ? 'text' : item.type || 'text'"
            @input="onInput(item.name, $event)"
            @click="item.type === 'select' && openPicker(item)"
          >
            <template #right-icon>
              <van-icon
                v-if="item.rightIcon"
                :name="item.rightIcon"
                class="field-icon"
              />
              <van-icon
                v-else-if="item.type === 'select'"
                name="arrow-down"
                class="field-icon"
              />
            </template>
          </van-field>
        </template>
      </van-cell-group>

      <div v-if="hasOptionalFields(section)" class="optional-section">
        <transition name="fade">
          <div v-show="sectionExpanded[sectionIndex]" class="optional-content">
            <van-cell-group inset>
              <template v-for="(item, itemIndex) in getOptionalFields(section)">
                <slot
                  v-if="$slots[item.name] || item.type === 'slot'"
                  :name="item.name"
                >
                </slot>
                <template v-else-if="item.type === 'switch'">
                  <van-field
                    :key="'optional-' + sectionIndex + '-' + itemIndex"
                    :name="item.name"
                    :label="item.label"
                    :required="!!item.required"
                    :disabled="item.disabled"
                  >
                    <template #input>
                      <van-switch
                        v-model="formData[item.name]"
                        :disabled="item.disabled"
                        size="24px"
                      />
                    </template>
                  </van-field>
                </template>
                <van-field
                  v-else
                  :key="'optional-' + sectionIndex + '-' + itemIndex"
                  :name="item.name"
                  :label="item.label"
                  :placeholder="getPlaceholder(item)"
                  :required="!!item.required"
                  :disabled="item.disabled"
                  :readonly="item.type === 'select' || item.readonly"
                  :rules="item.rules"
                  :value="
                    item.type === 'select'
                      ? item.options.find(
                          (opt) => opt.value === formData[item.name]
                        )?.text || ''
                      : formData[item.name]
                  "
                  :type="item.type === 'select' ? 'text' : item.type || 'text'"
                  @input="onInput(item.name, $event)"
                  @click="item.type === 'select' && openPicker(item)"
                >
                </van-field>
              </template>
            </van-cell-group>
          </div>
        </transition>
      </div>
    </div>

    <!-- 选择器弹窗 -->
    <van-popup v-model:show="pickerVisible" position="bottom">
      <van-picker
        :value="currentPickerValue"
        :columns="currentPickerColumns"
        show-toolbar
        @confirm="onPickerConfirm"
        @cancel="pickerVisible = false"
      />
    </van-popup>
  </div>
</template>

<script>
export default {
  name: "DynamicForm",
  props: {
    formConfig: {
      type: Array,
      required: true,
    },
    value: {
      type: Object,
      required: true,
    },
    expandedSections: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    const expandedMap = {};
    this.formConfig.forEach((_, index) => {
      expandedMap[index] = this.expandedSections.includes(index);
    });
    return {
      sectionExpanded: expandedMap,
      pickerVisible: false,
      currentItem: null,
      currentPickerValue: null,
      currentPickerColumns: [],
    };
  },
  computed: {
    formData: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit("input", val);
      },
    },
  },
  methods: {
    toggleOptional(index) {
      this.sectionExpanded[index] = !this.sectionExpanded[index];
    },
    getRequiredFields(section) {
      return section.form.filter((item) => item.required === true);
    },
    hasRequiredFields(section) {
      return this.getRequiredFields(section).length > 0;
    },
    getOptionalFields(section) {
      return section.form.filter((item) => item.required !== true);
    },
    hasOptionalFields(section) {
      return this.getOptionalFields(section).length > 0;
    },
    getPlaceholder(item) {
      if (item.placeholder) {
        return item.placeholder;
      }
      return item.required
        ? `请输入${item.label}`
        : `请输入${item.label}（选填）`;
    },
    openPicker(item) {
      this.currentItem = item;
      this.currentPickerValue = this.formData[item.name];
      this.currentPickerColumns = item.options.map((opt) => ({
        text: opt.text,
        value: opt.value,
      }));

      this.pickerVisible = true;
    },
    onInput(name, value) {
      this.formData = {
        ...this.formData,
        [name]: value,
      };
    },
    onPickerConfirm(value) {
      this.formData = {
        ...this.formData,
        [this.currentItem.name]: value.value,
      };
      this.pickerVisible = false;
      this.currentItem = null;
    },
    // 表单必填校验
    validate() {
      const errors = [];
      this.formConfig.forEach((section) => {
        section.form.forEach((item) => {
          //  必填校验
          if (item.required) {
            const value = this.formData[item.name];
            if (value === undefined || value === null || value === '') {
              errors.push(`${item.label}不能为空`);
            }
          }
          // 自定义规则校验
          if (item.rules && Array.isArray(item.rules)) {
            const value = this.formData[item.name];
            for (const rule of item.rules) {
              if (rule.validator && typeof rule.validator === 'function') {
                if (!rule.validator(value)) {
                  errors.push(rule.message);
                  break;
                }
              }
            }
          }
        });
      });
      return {
        valid: errors.length === 0,
        errors,
      };
    },
  },
};
</script>

<style lang="less" scoped>
.dynamic-form {
  .form-section {
    margin-bottom: 16px;
    background: #fff;
    border-radius: 8px;
    overflow: hidden;
    padding: 12px;
  }

  .section-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 12px;
    cursor: pointer;

    &:hover {
      opacity: 0.8;
    }
  }

  .section-title {
    font-size: 16px;
    font-weight: bold;
    color: #000000;
  }

  .toggle-icon {
    color: #969799;
    transition: transform 0.3s;
  }

  .field-icon {
    color: #969799;
  }

  .optional-section {
    .optional-content {
      padding-top: 12px;
    }
  }
}
</style>
